import api from '../services/api';

export enum MaterialType {
  FABRIC = 'FABRIC',
  LACE = 'LACE',
  BUTTON = 'BUTTON'
}

export enum JobType {
  STITCHING = 'STITCHING',
  PRESS = 'PRESS'
}

export interface Purchase {
  id: number;
  purchase_id?: string;
  vendor_id: number;
  invoice_id: string;
  invoice_date: string;
  received_date: string;
  total_amount: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  company_id: number;
  material_inwards?: MaterialInward[];
  vendor?: {
    id: number;
    vendor_id?: string;
    registered_name: string;
    alias?: string;
  };
}

export interface CreatePurchaseDTO {
  purchase_id?: string;
  vendor_id: number;
  invoice_id: string;
  invoice_date: string;
  received_date: string;
  total_amount: number;
  notes?: string;
  material_inwards: CreateMaterialInwardDTO[];
  po_id?: number | null;
  po_item_fulfillments?: Array<{
    po_item_id: number;
    material_index: number;
    quantity: number;
  }>;
}

export interface MaterialInward {
  id: number;
  material_code: string;
  purchase_id: number;
  material_type: MaterialType;
  material_quality?: string;
  job_type?: JobType;
  quantity: number;
  uom: string;
  rate: number;
  amount: number;
  image_url?: string;
  is_sample: boolean;
  available_qty: number;
  created_at: string;
  updated_at: string;
  company_id: number;
  purchase?: Purchase;
  fabric_width?: number | null;
  fabric_L?: number | null;
}

export interface CreateMaterialInwardDTO {
  id?: number;
  material_code?: string;
  material_type: MaterialType;
  quantity: number;
  uom: string;
  rate: number;
  amount: number;
  material_quality?: string;
  is_sample?: boolean;
  image_url?: string;
  fabric_width?: number | null;
  fabric_L?: number | null;
  // When creating a Purchase from a PO, we tag the row with the linked PO item id
  po_item_id?: number;
}

export interface MaterialUsageDTO {
  used_quantity: number;
}

export const purchaseApi = {
  getAll: async (params?: any) => {
    return await api.get('/purchases', { params });
  },
  
  getById: async (id: number) => {
    return await api.get<Purchase>(`/purchases/${id}`);
  },
  
  create: async (data: CreatePurchaseDTO) => {
    return await api.post<Purchase>('/purchases', data);
  },
  
  update: async (id: number, data: CreatePurchaseDTO) => {
    return await api.put<Purchase>(`/purchases/${id}`, data);
  },
  
  delete: async (id: number) => {
    return await api.delete(`/purchases/${id}`);
  }
};

export const materialApi = {
  getAll: async (params?: { type?: MaterialType, is_sample?: boolean }) => {
    return await api.get<MaterialInward[]>('/materials', { params });
  },
  
  getById: async (id: number) => {
    return await api.get<MaterialInward>(`/materials/${id}`);
  },
  
  updateUsage: async (id: number, data: MaterialUsageDTO) => {
    return await api.patch<MaterialInward>(`/materials/${id}/usage`, data);
  },
  
  getInventorySummary: async (params?: { page?: number, pageSize?: number, q?: string, vendor_id?: number }) => {
    return await api.get<any>('/materials/summary/inventory', { params });
  }
};

export interface Material {
  id: number;
  material_code: string;
  purchase_id: number;
  material_type: 'FABRIC' | 'LACE' | 'BUTTON';
  material_quality: string | null;
  job_type: 'STITCHING' | 'PRESS' | null;
  quantity: number;
  uom: string;
  rate: number;
  amount: number;
  image_url: string | null;
  is_sample: boolean;
  available_qty: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
  company_id: number;
}

export const materialsApi = {
  // Get all materials
  getAll: async () => {
    try {
      const response = await api.get('/materials');
      return response.data;
    } catch (error) {
      console.error('Error fetching materials:', error);
      throw error;
    }
  },

  // Get a specific material by ID
  getById: async (id: number) => {
    try {
      const response = await api.get(`/materials/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching material with ID ${id}:`, error);
      throw error;
    }
  },

  // Get materials by type
  getByType: async (type: 'FABRIC' | 'LACE' | 'BUTTON') => {
    try {
      const response = await api.get(`/materials/type/${type}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching materials of type ${type}:`, error);
      throw error;
    }
  },

  // Get materials with available quantity greater than 0
  getAvailable: async () => {
    try {
      const response = await api.get('/materials/available');
      return response.data;
    } catch (error) {
      console.error('Error fetching available materials:', error);
      throw error;
    }
  },

  // Get distinct material types from database
  getDistinctTypes: async () => {
    try {
      const response = await api.get('/materials/types/distinct');
      return response.data;
    } catch (error) {
      console.error('Error fetching material types:', error);
      throw error;
    }
  },

  // Get distinct vendors (suppliers) for materials
  getDistinctVendors: async () => {
    try {
      const response = await api.get('/materials/vendors/distinct');
      return response.data;
    } catch (error) {
      console.error('Error fetching material vendors:', error);
      throw error;
    }
  }
};

// Inventory Adjustment Types
export enum AdjustmentType {
  RETURN = 'RETURN',
  DAMAGED = 'DAMAGED',
  STOCK_AUDIT = 'STOCK_AUDIT',
  CORRECTION = 'CORRECTION',
  OTHER = 'OTHER'
}

export interface InventoryAdjustment {
  id: number;
  material_id: number;
  adjustment_type: AdjustmentType;
  quantity_change: number;
  previous_qty: number;
  new_qty: number;
  reason?: string;
  reference_no?: string;
  adjusted_by: string;
  adjusted_at: string;
  company_id: number;
  material?: {
    id: number;
    material_code: string;
    material_type: string;
    material_quality?: string;
    uom: string;
    image_url?: string;
  };
}

export interface CreateAdjustmentDTO {
  material_id: number;
  adjustment_type: AdjustmentType;
  new_qty: number;
  reason?: string;
  reference_no?: string;
}

export interface BulkAdjustmentItem {
  material_id: number;
  new_qty: number;
}

export interface CreateBulkAdjustmentDTO {
  adjustment_type: AdjustmentType;
  reason?: string;
  reference_no?: string;
  adjustments: BulkAdjustmentItem[];
}

export interface BulkAdjustmentResult {
  success: number;
  failed: number;
  results: Array<{
    material_id: number;
    material_code: string;
    status: 'success' | 'error';
    error?: string;
    previous_qty?: number;
    new_qty?: number;
  }>;
}

export interface AdjustmentSummary {
  totalAdjustments: number;
  netQuantityChange: number;
  byType: Array<{
    type: AdjustmentType;
    count: number;
    totalChange: number;
  }>;
}

export const inventoryAdjustmentApi = {
  // Get all adjustments with pagination
  getAll: async (params?: {
    page?: number;
    pageSize?: number;
    material_id?: number;
    adjustment_type?: AdjustmentType;
    from_date?: string;
    to_date?: string;
  }) => {
    const response = await api.get('/inventory-adjustments', { params });
    return response.data;
  },

  // Get adjustments for a specific material
  getByMaterial: async (materialId: number) => {
    const response = await api.get(`/inventory-adjustments/material/${materialId}`);
    return response.data as InventoryAdjustment[];
  },

  // Create a new adjustment
  create: async (data: CreateAdjustmentDTO) => {
    const response = await api.post('/inventory-adjustments', data);
    return response.data;
  },

  // Get adjustment summary/stats
  getSummary: async (params?: { from_date?: string; to_date?: string }) => {
    const response = await api.get('/inventory-adjustments/summary', { params });
    return response.data as AdjustmentSummary;
  },

  // Bulk adjust multiple materials at once
  createBulk: async (data: CreateBulkAdjustmentDTO): Promise<BulkAdjustmentResult> => {
    const response = await api.post('/inventory-adjustments/bulk', data);
    return response.data as BulkAdjustmentResult;
  },
};

// Inventory Change Log
export interface InventoryChangeLog {
  id: number;
  material_id: number;
  change_type: string;
  quantity_change: number;
  previous_qty: number;
  new_qty: number;
  reference_type: string;
  reference_id: number;
  reference_code?: string;
  design_no?: string;
  notes?: string;
  effective_date: string;
  created_by: string;
  created_at: string;
  company_id: number;
  material?: {
    id: number;
    material_code: string;
    material_type: string;
    material_quality?: string;
    uom: string;
    image_url?: string;
  };
}

export const inventoryChangeLogApi = {
  // Get change log for a specific material
  getByMaterial: async (materialId: number, params?: {
    page?: number;
    pageSize?: number;
    change_type?: string;
    from_date?: string;
    to_date?: string;
  }) => {
    const response = await api.get(`/inventory-adjustments/change-log/material/${materialId}`, { params });
    return response.data;
  },

  // Get change log for a design
  getByDesign: async (designNo: string, params?: { page?: number; pageSize?: number }) => {
    const response = await api.get(`/inventory-adjustments/change-log/design/${designNo}`, { params });
    return response.data;
  },

  // Get change log for a job work
  getByJobWork: async (jobWorkId: number) => {
    const response = await api.get(`/inventory-adjustments/change-log/job-work/${jobWorkId}`);
    return response.data;
  },
};

export default {
  purchase: purchaseApi,
  material: materialApi,
  materials: materialsApi,
  inventoryAdjustment: inventoryAdjustmentApi,
  inventoryChangeLog: inventoryChangeLogApi
}; 